import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxUser } from '@/contexts/UserContext';
import { createSearchParams, useNavigate } from 'react-router';
import { ctxSearch } from '@/contexts/SearchContext';
import MultiTargetSelector from '../common/selection/MultiTargetSelector';
import { GridColDef, GridRenderCellParams, GridRowClassNameParams } from '@mui/x-data-grid';
import { provideTargetIcon } from '../common/IconProvider';
import {
  isTargetInactiveByState,
  TargetNameAndDescription,
  targetNameAndDescriptionStyle,
} from '../common/TargetNameAndDescription';
import { MenuPopover } from './MenuPopover';
import { Box, IconButton } from '@mui/material';
import { CenterFocusWeak, MoreVert, ShareOutlined } from '@mui/icons-material';
import { ReactElement, useState, useContext } from 'react';
import { MinimalTarget } from '@/generated/model';
import { getTargetRowAction } from '@/components/tracking/targetRowAction';

export default function TrackingTargetSelector({
  trackingLimit,
  closeTargetSelector,
}: {
  trackingLimit: number;
  closeTargetSelector: () => void;
}) {
  const ctx = useContext(ctxPluto);
  const userCtx = useContext(ctxUser);
  const searchCtx = useContext(ctxSearch);
  const [popover, setPopover] = useState<ReactElement | undefined>(undefined);
  const navigate = useNavigate();
  if (!ctx || !searchCtx) return;

  function onClose(full: boolean) {
    setPopover(undefined);
    if (full) closeTargetSelector();
  }

  function findTarget(id: number) {
    if (!ctx || !ctx.fullTargets) return undefined;

    return [...ctx.fullTargets].find((t) => t.target.id === id);
  }

  const selectedTargets = ctx.selectedTargets;
  const columns: GridColDef[] = [
    {
      field: 'category',
      headerName: 'Category',
      cellClassName: 'target-cell-category',
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => {
        const target = params.row;
        return (
          <div className="target-cell" data-qa-element-id="target_icon">
            <img className="idata-target-icon" src={provideTargetIcon(target.category)} />
          </div>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      cellClassName: 'target-cell-name',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const isChecked = selectedTargets
          ? selectedTargets.find((target) => target.id === params.row.id) != undefined
          : false;
        const target = params.row;
        let hasValidSatatus = false;
        if (isChecked) {
          const status = ctx.selectedTargetStatus.get(target.id);
          hasValidSatatus = status === 'validstatus';
        }
        return (
          <div className="target-cell">
            <TargetNameAndDescription
              targetName={target.name}
              targetDescription={target.description}
              styleClassName={targetNameAndDescriptionStyle(
                isChecked,
                hasValidSatatus,
                isTargetInactiveByState(target)
              )}
            />
          </div>
        );
      },
    },
    {
      field: 'moreInfo',
      headerName: 'MoreInfo',
      cellClassName: 'target-cell-more',
      flex: 0,
      renderCell: (params: GridRenderCellParams) => {
        const isChecked = selectedTargets
          ? selectedTargets.find((target) => target.id === params.row.id) != undefined
          : false;
        const target = params.row;
        const status = ctx.selectedTargetStatus.get(target.id);
        const action = getTargetRowAction({
          isSelected: isChecked,
          targetState: target.state,
          status,
          canShare: !userCtx?.isToken() && !!userCtx?.hasExtraFeature('extrafeature_pluto_target_share'),
        });

        if (action === 'share') {
          return (
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ width: 40 }} />

              <IconButton
                data-qa-element-id="target_share_button"
                onClick={() => {
                  navigate({
                    pathname: './share',
                    search: createSearchParams({
                      targetId: String(target.id),
                      targetName: target.name,
                    }).toString(),
                  });
                  onClose(false);
                }}
              >
                <ShareOutlined sx={{ color: 'var(--mui-palette-action-active)' }} />
              </IconButton>
            </Box>
          );
        }

        if (action !== 'targetActions') {
          return null;
        }

        return (
          <Box sx={{ display: 'flex' }}>
            <IconButton
              data-qa-element-id="target_target_center_button"
              onClick={() => {
                ctx?.setFocusedTarget(findTarget(target.id));
                ctx?.setPopupTargetId(target.id);
                onClose(true);
              }}
            >
              <CenterFocusWeak sx={{ color: 'var(--mui-palette-action-active)' }} />
            </IconButton>

            <IconButton
              data-qa-element-id="target_options_button"
              className="tracking-body-target-list-item-more"
              onClick={(evt) => {
                const popover = <MenuPopover element={evt.currentTarget} target={target} onClose={onClose} />;
                setPopover(popover);
                evt.stopPropagation();
              }}
            >
              <MoreVert sx={{ color: 'var(--mui-palette-action-active)' }} />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  function rowClassProvider(params: GridRowClassNameParams<MinimalTarget>) {
    const targetId = params.row.id;
    const status = ctx?.selectedTargetStatus.get(targetId);
    if (!status || status == 'unknown' || status == 'nostatus') {
      return 'no-valid-status-row';
    }
    return '';
  }

  return (
    <>
      <MultiTargetSelector
        selectedTargets={ctx.selectedTargets}
        setSelectedTargets={ctx.setSelectedTargets}
        searchText={searchCtx.searchText}
        setSearchText={searchCtx.setSearchText}
        maxElementNumber={trackingLimit}
        columnsOverride={columns}
        rowClassProvider={rowClassProvider}
      />
      {popover}
    </>
  );
}
